import { find, map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { ArrowLeft, Link2, Plus } from 'lucide-react'
import { CreateTicketModal } from './CreateTicketModal'
import Loader from '../../../common/components/Loader'
import {
  Currency,
  Languages,
  Ticket,
  TicketCategory,
  TicketList,
} from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getCurrencies,
  getSettingsLanguages,
} from '../../../core/services/festival-settings'
import {
  IFestivalMenuItem,
  getFestivalsMenu,
} from '../../../core/services/menu'
import {
  getAllCategories,
  getTicketById,
  getTicketsByCategoryId,
  publishTicket,
  unpublishTicket,
} from '../../../core/services/tickets'
import store, { useAppDispatch, useAppSelector } from '../../../store'
import { setCategoryInRedux } from '../../ducks'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'

export const TicketPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
  } = useTableParams()

  const [editCreateModal, setEditCreateModal] = useState(false)
  const [currentTicket, setCurrentTicket] = useState<Ticket>()
  const [tickets, setTickets] = useState<TicketList[]>([])
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const { festivalName, editionId, categoryId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name
  const { hasEditPermission } = useCheckPermission({ page: 'TICKETS' })
  const navigate = useNavigate()
  const [allCategories, setAllCategories] = useState<TicketCategory[]>([])
  const dispatch = useAppDispatch()
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [festivals, setFestivals] = useState<IFestivalMenuItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const [currencyResponse, allLanguagesResponse, festivalsResponse] = await Promise.allSettled([
          getCurrencies(),
          getSettingsLanguages(),
          getFestivalsMenu()
        ])

        if (currencyResponse.status === 'fulfilled') {
          setCurrency(currencyResponse.value.data.defaultCurrency)
        }
        if (allLanguagesResponse.status === 'fulfilled') {
          setAllLanguages(allLanguagesResponse.value.data)
        }

        if (festivalsResponse.status === 'fulfilled') {
          setFestivals(festivalsResponse.value.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getTicketsByCategoryId(
        categoryId || '',
        { size, sortBy, page, sortDirection },
        false,
      )
      setTickets(response.data.content)
      setTotalElemenets(response.data.totalElements)

      const response2 = await getAllCategories({ size: 100 })
      setAllCategories(response2.data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [sortBy, categoryId, size, page, editionId, festivalName, sortDirection])

  useEffect(() => {
    getData()
  }, [getData])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handlePublish = (isCheck: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await publishTicket(isCheck)
        const response = await getTicketsByCategoryId(categoryId || '', {
          sortBy,
          size,
          page,
        }, false)
        setTickets(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (isCheck: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await unpublishTicket(isCheck)
        const response = await getTicketsByCategoryId(categoryId || '', {
          sortBy,
          size,
          page
        }, false)
        setTickets(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const columns: IColumn[] = [
    {
      title: '',
      accessor: '',
      searchable: false,
      sortable: false,
      render: (row) => {
        const fest = find(
          festivals,
          (f) => !!f.editions.find((e) => e.id === editionId),
        )
        let festivalLink = fest ? fest.website : undefined
        if (!festivalLink && fest) {
          switch (fest.platformName) {
            case 'untold':
              festivalLink = 'https://untold.com'
              break
            case 'neversea':
              festivalLink = 'https://neversea.com'
              break
            case 'untold-dubai':
              festivalLink = 'https://untold.ae'
              break
            default:
              festivalLink = ''
          }
        }
        const link = `${festivalLink}/passes#${row.id.split('-')[4]}`
        return (
          <Button
            variant="secondary"
            className="w-10 h-10 p-0"
            onClick={() => {
              navigator.clipboard.writeText(link)
            }}
          >
            <Link2 className="w-4 h-4 text-monochrome-1" />
          </Button>
        )
      },
    },
    {
      title: 'ID',
      accessor: 'readableId',
      sortable: true,
    },
    {
      title: 'Name',
      accessor: 'name',
      searchable: true,
      sortable: true,
    },
    {
      title: 'Ordinal',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => (
        <Input
          value={row.ordinal}
          readOnly
          className="bg-monochrome-4 border-none"
        />
      ),
    },
    {
      title: 'Category',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => <span>{row.ticketCategory.name}</span>,
    },
    {
      title: 'Stock',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => <span>{row.stock < 0 ? 'Unlimited' : row.stock}</span>,
    },
    {
      title: 'Add-ons',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => (
        <div className="flex flex-col gap-2">
          {map(row.extras, (extra) => {
            return (
              <div key={extra.id} className="flex items-center gap-2">
                <Checkbox checked={extra.published} disabled />
                <span className="text-sm text-monochrome-2">{extra?.name}</span>
              </div>
            )
          })}
        </div>
      ),
    },
    {
      title: 'Day Ticket',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => <span>{row.day?.name}</span>,
    },
    {
      title: 'Options',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={['BOTH', 'FULL_PRICE', 'MONTHLY'].includes(
                row.paymentOption,
              )}
              disabled
            />
            <span className="text-sm text-monochrome-2">
              Payments: {row.paymentOption ? row.paymentOption : 'none'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={row.hasBonus} disabled />
            <span className="text-sm text-monochrome-2">Bonus</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={row.hasReferral} disabled />
            <span className="text-sm text-monochrome-2">Referral</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={row.hasDiscount} disabled />
            <span className="text-sm text-monochrome-2">Discount</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Price',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => (
        <span>
          {row.price} {currency?.symbol}
        </span>
      ),
    },
    {
      title: 'Published Date',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => <span>{formatDateTime(row.publishTimestamp)}</span>,
    },
    {
      title: 'Backstage',
      accessor: 'backstageProductId',
      sortable: true,
      render: (row) => (
        <div className="flex flex-col gap-2">
          <span>{row.backstageProductId}</span>
          <div className="flex items-center gap-2">
            <Checkbox checked={row.backstageSync} disabled />
            <span className="text-sm text-monochrome-2">Sync</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Published',
      accessor: '',
      searchable: true,
      sortable: true,
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublish([row.id])
            } else {
              handlePublish([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      sortable: true,
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (event) => {
            event.preventDefault()
            if (!hasEditPermission) {
              return
            }
            const response = await getTicketById(row.id)
            setCurrentTicket(response.data)
            setEditCreateModal(true)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Tickets"
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditCreateModal(true)
                  setCurrentTicket(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              className="w-11 h-11"
              onClick={() => {
                navigate(`/tickets/categories/${festivalName}/${editionId}`)
              }}
            >
              <ArrowLeft className="w-4 h-4 text-white shrink-0" />
            </Button>
            <PageTitle title="Tickets" />
          </div>
          <div className="min-w-[240px]">
            <SelectField
              label="Category"
              placeholder="Select Category"
              value={categoryId || 'none'}
              onValueChange={(value) => {
                if (value !== 'none' && editionId) {
                  dispatch(setCategoryInRedux(editionId, value))
                  navigate(
                    `/tickets/tickets/${festivalName}/${editionId}/${value}`,
                  )
                }
              }}
              options={[
                {
                  value: 'none',
                  label: 'Select Category',
                  disabled: true,
                },
                ...map(allCategories, (category) => ({
                  value: category.id,
                  label: category.name,
                })),
              ]}
              outline
            />
          </div>
        </div>
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={tickets}
          handleSortBy={handleSortBy}
          columnWidth="8%"
          handleSearch={(col, query) => {
            console.log(col, query)
          }}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateTicketModal
        visible={editCreateModal}
        currency={currency?.symbol || 'RON'}
        page={page}
        size={size}
        setTotalElemenets={setTotalElemenets}
        ticket={currentTicket}
        setVisible={setEditCreateModal}
        sortBy={sortBy}
        setTickets={setTickets}
        categoryId={categoryId || ''}
        category={find(allCategories, (category) => category.id === categoryId)}
        allCategories={allCategories}
        allLanguages={allLanguages}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
